import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useCallback, useState } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import { Breadcrumb } from "@/components/shared/Breadcrumb"
import { toggleSavedRitual } from "@/features/profile/services/saved-rituals.service"
import { IntentionFilters } from "../components/IntentionFilters"
import { IntentionHeader } from "../components/IntentionHeader"
import { IntentionRitualGrid } from "../components/IntentionRitualGrid"
import { IntentionSearchBar } from "../components/IntentionSearchBar"
import { useIntention } from "../hooks/useIntention"
import { useIntentionRituals } from "../hooks/useIntentionRituals"
import { useToggleIntention } from "../hooks/useToggleIntention"
import type { IntentionFiltersState } from "../types"

const DEFAULT_FILTERS: IntentionFiltersState = {
  search: "",
  sort: "trending",
  price: "all",
  format: "all",
  duration: "all",
}

const VALID_SORTS: IntentionFiltersState["sort"][] = ["trending", "newest"]
const VALID_PRICES: IntentionFiltersState["price"][] = ["all", "free", "paid"]
const VALID_FORMATS: IntentionFiltersState["format"][] = [
  "all",
  "IN_PERSON",
  "VIRTUAL_COURSE",
  "SELF_GUIDED",
  "SELF_GUIDED_COURSE",
]
const VALID_DURATIONS: IntentionFiltersState["duration"][] = ["all", "short", "medium", "long"]

function clamp<T extends string>(value: string | null, allowed: T[], fallback: T): T {
  if (value === null) return fallback
  return allowed.includes(value as T) ? (value as T) : fallback
}

function parseFiltersFromParams(params: URLSearchParams): IntentionFiltersState {
  return {
    search: params.get("search") ?? DEFAULT_FILTERS.search,
    sort: clamp(params.get("sort"), VALID_SORTS, DEFAULT_FILTERS.sort),
    price: clamp(params.get("price"), VALID_PRICES, DEFAULT_FILTERS.price),
    format: clamp(params.get("format"), VALID_FORMATS, DEFAULT_FILTERS.format),
    duration: clamp(params.get("duration"), VALID_DURATIONS, DEFAULT_FILTERS.duration),
  }
}

function filtersToParams(filters: IntentionFiltersState): Record<string, string> {
  const params: Record<string, string> = {}
  if (filters.search) params.search = filters.search
  if (filters.sort !== "trending") params.sort = filters.sort
  if (filters.price !== "all") params.price = filters.price
  if (filters.format !== "all") params.format = filters.format
  if (filters.duration !== "all") params.duration = filters.duration
  return params
}

export function IntentionDetailScreen() {
  const { intentionId: slug } = useParams<{ intentionId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()

  const [filters, setFilters] = useState<IntentionFiltersState>(() =>
    parseFiltersFromParams(searchParams),
  )

  const { intention, isLoading: intentionLoading } = useIntention(slug ?? "")
  const {
    rituals,
    hasNextPage,
    isLoading: ritualsLoading,
    isFetchingNextPage,
    fetchNextPage,
  } = useIntentionRituals(slug ?? "", filters)
  const toggleMutation = useToggleIntention(slug ?? "")
  const queryClient = useQueryClient()
  const bookmarkMutation = useMutation({
    mutationFn: (ritualId: string) => toggleSavedRitual(ritualId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["intentionRituals", slug] })
      queryClient.invalidateQueries({ queryKey: ["saved-rituals"] })
    },
  })

  const handleFiltersChange = useCallback(
    (newFilters: IntentionFiltersState) => {
      setFilters(newFilters)
      setSearchParams(filtersToParams(newFilters), { replace: true })
    },
    [setSearchParams],
  )

  const handleSearchChange = useCallback(
    (query: string) => {
      handleFiltersChange({ ...filters, search: query })
    },
    [filters, handleFiltersChange],
  )

  const handleLoadMore = useCallback(() => {
    fetchNextPage()
  }, [fetchNextPage])

  const handleBookmark = useCallback(
    (ritualId: string) => {
      bookmarkMutation.mutate(ritualId)
    },
    [bookmarkMutation],
  )

  const handleToggleIntention = useCallback(() => {
    if (intention) {
      toggleMutation.mutate(intention.id)
    }
  }, [intention, toggleMutation])

  if (intentionLoading) {
    return (
      <div className="bg-background min-h-screen">
        <div className="py-6">
          <div className="h-4 w-48 bg-white/10 rounded animate-pulse" />
          <div className="h-10 w-64 bg-white/10 rounded animate-pulse mt-4" />
          <div className="h-4 w-96 bg-white/10 rounded animate-pulse mt-3" />
        </div>
      </div>
    )
  }

  if (!intention) {
    return (
      <div className="bg-background min-h-screen flex items-center justify-center">
        <p className="text-white/60 text-lg">Intention not found.</p>
      </div>
    )
  }

  const breadcrumbItems = [
    { label: "Home", href: "/" },
    { label: "Learn & Practice", href: "/learn-and-practice" },
    { label: intention.name },
  ]

  return (
    <div className="min-h-screen max-w-360 mx-auto px-5 md:px-17.5">
      <Breadcrumb items={breadcrumbItems} className="pt-5 pb-2" />

      <IntentionHeader
        intention={intention}
        isToggling={toggleMutation.isPending}
        onToggle={handleToggleIntention}
      />

      <IntentionSearchBar onSearchChange={handleSearchChange} />

      <IntentionFilters
        filters={filters}
        intentionName={intention.name}
        onChange={handleFiltersChange}
      />

      <IntentionRitualGrid
        rituals={rituals}
        isLoading={ritualsLoading || isFetchingNextPage}
        hasMore={hasNextPage}
        onLoadMore={handleLoadMore}
        onBookmark={handleBookmark}
      />
    </div>
  )
}
